/**
 * DialogForm - Custom compound dialog component (NOT from shadcn).
 *
 * Provides one consistent "create / edit" surface across the app: a centered
 * modal with shared chrome (header, scrollable body, footer), content-driven
 * height, and a configurable max-width.
 *
 * It is a *compound* component: the root owns the outside-world concerns (the
 * Radix dialog, escape-blocking, the in-dialog toast viewport, the close
 * handler) and exposes that via context, while callers compose the pieces with
 * children:
 *
 *   <DialogForm showDialog={open} onOpenChange={setOpen} size="md" onClose={close}>
 *     <DialogForm.Header>
 *       <DialogForm.Title icon={<PiFunnel className="size-5" />}>
 *         Create filter
 *       </DialogForm.Title>
 *       <DialogForm.Description>...</DialogForm.Description>
 *     </DialogForm.Header>
 *     <DialogForm.Body isLoading={isLoading} hasLoaded={hasLoaded}>
 *       <MyForm />
 *     </DialogForm.Body>
 *     <DialogForm.Footer>
 *       <DialogForm.Cancel>Cancel</DialogForm.Cancel>
 *       <DialogForm.Submit id="my-submit" disabled={isSubmitting} onSubmit={submit}>
 *         Submit
 *       </DialogForm.Submit>
 *     </DialogForm.Footer>
 *   </DialogForm>
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from '@ais-ui/utils';
import { PiLock, PiWarningCircle } from 'react-icons/pi';
import { LoadingSpinner } from './LoadingSpinner';
import { Button } from './Button';
import { ScrollArea } from './ScrollArea';
import { DialogToastRefContext } from './Toast/DialogToastContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';

/**
 * Controls the dialog's max-width. Height is always content-driven (the box hugs
 * its content and only scrolls once it would exceed the viewport cap), so size is
 * about how wide the form should be for the amount of content it holds.
 */
export type DialogFormSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASS: Record<DialogFormSize, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-xl',
  lg: 'sm:max-w-3xl',
  xl: 'sm:max-w-5xl',
};

interface DialogFormContextValue {
  /** Close the dialog the same way the X / escape does (honours onClose). */
  close: () => void;
  /**
   * Register a request to block escape/close while a nested surface (e.g. an
   * open autocomplete dropdown) wants to swallow Escape itself. Returns an
   * unregister function. Blocks are ref-counted, so multiple concurrent
   * blockers compose correctly.
   */
  registerEscapeBlock: () => () => void;
}

const DialogFormContext = React.createContext<DialogFormContextValue | null>(
  null,
);

const useDialogFormContext = (component: string): DialogFormContextValue => {
  const context = React.useContext(DialogFormContext);
  if (!context) {
    throw new Error(`${component} must be used within a <DialogForm>.`);
  }
  return context;
};

/**
 * Block the surrounding dialog from closing on Escape/overlay while
 * `shouldBlock` is true. Used by child components that want to handle Escape
 * themselves (e.g. close an open autocomplete dropdown) without dismissing the
 * whole dialog. Scoped to the owning <DialogForm> via context and ref-counted.
 */
export const useBlockDialogEscape = (shouldBlock: boolean) => {
  const { registerEscapeBlock } = useDialogFormContext('useBlockDialogEscape');
  useEffect(() => {
    if (!shouldBlock) return;
    return registerEscapeBlock();
  }, [shouldBlock, registerEscapeBlock]);
};

interface DialogFormProps {
  showDialog: boolean;
  onOpenChange: (show: boolean) => void;
  /**
   * Optional id forwarded to the underlying dialog panel. Gives callers (and
   * E2E tests) a stable selector for the dialog surface.
   */
  id?: string;
  /** Controls the dialog max-width. Defaults to 'lg'. */
  size?: DialogFormSize;
  /**
   * When true the dialog takes a stable height (85vh) instead of sizing to its
   * content. Use for rail dialogs so switching sections doesn't resize the
   * dialog — the active pane scrolls internally instead.
   */
  fillHeight?: boolean;
  /**
   * Called when the dialog is closing (X, escape, overlay, or Cancel). When
   * provided it owns the close behaviour (e.g. an unsaved-changes warning);
   * otherwise the dialog falls back to `onOpenChange(false)`.
   */
  onClose?: () => void;
  className?: string;
  children?: React.ReactNode;
}

const DialogFormRoot = ({
  showDialog,
  onOpenChange,
  id,
  size = 'lg',
  fillHeight = false,
  onClose,
  className,
  children,
}: DialogFormProps) => {
  const dialogToastRef = useRef<HTMLDivElement>(null);
  const escapeBlockCountRef = useRef(0);
  const [escapeBlocked, setEscapeBlocked] = useState(false);

  const registerEscapeBlock = useCallback(() => {
    escapeBlockCountRef.current += 1;
    setEscapeBlocked(true);
    return () => {
      escapeBlockCountRef.current -= 1;
      setEscapeBlocked(escapeBlockCountRef.current > 0);
    };
  }, []);

  const close = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      onOpenChange(false);
    }
  }, [onClose, onOpenChange]);

  const contextValue = useMemo<DialogFormContextValue>(
    () => ({ close, registerEscapeBlock }),
    [close, registerEscapeBlock],
  );

  return (
    <Dialog
      open={showDialog}
      onOpenChange={(open) => {
        // Only allow closing if escape is not blocked by a child component.
        if (!open && escapeBlocked) {
          return; // A nested surface (e.g. autocomplete) owns Escape.
        }
        onOpenChange(open);
        if (!open && onClose) {
          onClose();
        }
      }}
    >
      <DialogContent
        id={id}
        className={cn(
          'flex max-h-[85vh] flex-col pt-8 pl-12 pr-8',
          // Stable height = 49rem, reusing the same `max-h-[85vh]` viewport
          // cap every dialog already has above. `height` + `max-height` (no
          // `min-height`) resolve as min(height, max-height) natively, so the
          // shared cap always wins on short viewports — no second 85vh
          // literal needed. (The old `min-h-[49rem]` conflicted with
          // max-height instead: min-height wins ties, which pushed the
          // dialog off-screen.) The rail nav and active pane both scroll
          // internally, so shrinking below 49rem is safe.
          fillHeight && 'h-[49rem]',
          SIZE_CLASS[size],
          className,
        )}
        onClose={onClose}
        onEscapeKeyDown={(event) => {
          // Own the Escape key here so the block guard applies whether or not
          // the dialog uses a custom onClose (e.g. exit warnings). Otherwise
          // DialogContent calls onClose directly on Escape, bypassing any
          // registered escape block (e.g. an open autocomplete).
          event.preventDefault();
          if (escapeBlocked) {
            return; // A nested surface (e.g. autocomplete) owns Escape.
          }
          close();
        }}
      >
        <DialogFormContext.Provider value={contextValue}>
          <DialogToastRefContext.Provider value={dialogToastRef}>
            <div ref={dialogToastRef} className="relative contents">
              {children}
            </div>
          </DialogToastRefContext.Provider>
        </DialogFormContext.Provider>
      </DialogContent>
    </Dialog>
  );
};

const DialogFormHeader = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  // -mt-px pulls the whole header block ~1px closer to the top edge.
  <DialogHeader className={cn('shrink-0 -mt-px', className)} {...props} />
);

interface DialogFormTitleProps extends React.ComponentProps<
  typeof DialogTitle
> {
  /** Optional icon rendered to the left of the title. */
  icon?: React.ReactNode;
}

const DialogFormTitle = ({
  icon,
  children,
  className,
  ...props
}: DialogFormTitleProps) => {
  if (!icon) {
    return (
      <DialogTitle className={className} {...props}>
        {children}
      </DialogTitle>
    );
  }
  return (
    <div className="flex items-center gap-2">
      {icon}
      {/* Optical correction: nudge the title up 2px so its cap-height aligns
          with the icon center (leading-none makes it sit slightly low). */}
      <DialogTitle className={cn('-mt-0.5', className)} {...props}>
        {children}
      </DialogTitle>
    </div>
  );
};

const DialogFormDescription = DialogDescription;

interface DialogFormBodyProps extends React.ComponentProps<'div'> {
  isLoading?: boolean;
  hasLoaded?: boolean;
  observeResize?: boolean;
  /**
   * When false the body does not add its own scroll container; the child owns
   * scrolling (e.g. a rail whose nav stays fixed while the pane scrolls). The
   * body then just fills the available height. Defaults to true.
   */
  scroll?: boolean;
}

const DialogFormBody = ({
  isLoading = false,
  hasLoaded = true,
  observeResize = true,
  scroll = true,
  className,
  children,
  ...props
}: DialogFormBodyProps) => {
  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (!hasLoaded) {
    return null;
  }

  if (!scroll) {
    return (
      <div
        className={cn('flex min-h-0 flex-auto flex-col', className)}
        {...props}
      >
        {children}
      </div>
    );
  }

  return (
    <ScrollArea
      className="min-h-0 flex-auto pr-4"
      observeResize={observeResize}
    >
      <div className={cn('space-y-6 px-px pt-px pb-1', className)} {...props}>
        {children}
      </div>
    </ScrollArea>
  );
};

const DialogFormFooter = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  <DialogFooter
    className={cn(
      'shrink-0 -ml-12 -mr-8 -mb-6 mt-2 border-t bg-muted/40 px-8 py-4 pl-12',
      className,
    )}
    {...props}
  />
);

const DialogFormCancel = ({
  onClick,
  children,
  ...props
}: React.ComponentProps<typeof Button>) => {
  const { close } = useDialogFormContext('DialogForm.Cancel');

  return (
    <Button
      variant="outline"
      {...props}
      type="button"
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          close();
        }
      }}
    >
      {children}
    </Button>
  );
};

interface DialogFormSubmitProps extends Omit<
  React.ComponentProps<typeof Button>,
  'onClick'
> {
  /** The submit handler. Concurrent invocations are guarded against. */
  onSubmit: () => void | Promise<void>;
}

const DialogFormSubmit = ({
  onSubmit,
  children,
  ...props
}: DialogFormSubmitProps) => {
  // Synchronous in-flight guard to keep submissions idempotent. `disabled` is
  // driven by React state, which only updates on the next render; a slow backend
  // leaves a window where rapid clicks fire the handler multiple times (e.g.
  // creating duplicate filters). This ref blocks concurrent invocations
  // immediately, regardless of when `disabled` updates.
  const isSubmitInFlight = useRef(false);

  const handleClick = useCallback(async () => {
    if (isSubmitInFlight.current) {
      return;
    }
    isSubmitInFlight.current = true;
    try {
      await onSubmit();
    } catch {
      // Errors are surfaced by the submit handler itself (e.g. toast/onError);
      // swallow here so the guard always resets and never leaves an unhandled
      // rejection.
    } finally {
      isSubmitInFlight.current = false;
    }
  }, [onSubmit]);

  return (
    <Button {...props} type="submit" onClick={handleClick}>
      {children}
    </Button>
  );
};

interface DialogFormSectionMeta {
  id: string;
  label: React.ReactNode;
  group: string;
  order: number;
  summary?: React.ReactNode;
  locked?: { reason: React.ReactNode };
  hasError?: boolean;
}

interface DialogFormRailContextValue {
  activeId: string | null;
  setActive: (id: string) => void;
  selectSection: (id: string) => void;
  register: (meta: DialogFormSectionMeta) => () => void;
  sections: DialogFormSectionMeta[];
}

const DialogFormRailContext =
  React.createContext<DialogFormRailContextValue | null>(null);

export function useDialogFormRail(): {
  activeId: string | null;
  setActive: (id: string) => void;
  selectSection: (id: string) => void;
} {
  const ctx = React.useContext(DialogFormRailContext);
  if (!ctx) {
    throw new Error(
      'useDialogFormRail must be used within a <DialogForm.Rail>.',
    );
  }
  const { activeId, setActive, selectSection } = ctx;
  return { activeId, setActive, selectSection };
}

interface DialogFormRailProps {
  groupOrder?: string[];
  groupLabels?: Record<string, React.ReactNode>;
  className?: string;
  /**
   * Optional controlled active-section id. When provided together with
   * `onActiveChange`, the rail becomes controlled and its active section is
   * driven by the parent — useful when the dialog content unmounts and
   * remounts (e.g. while picking a location on the map) but the selected
   * section must be preserved.
   */
  activeId?: string | null;
  onActiveChange?: (id: string) => void;
  children?: React.ReactNode;
}

const DialogFormRail = ({
  groupOrder,
  groupLabels,
  className,
  activeId: controlledActiveId,
  onActiveChange,
  children,
}: DialogFormRailProps) => {
  const [sections, setSections] = useState<DialogFormSectionMeta[]>([]);
  const isControlled = controlledActiveId !== undefined;
  const [internalActiveId, setInternalActiveId] = useState<string | null>(null);
  const activeId = isControlled ? controlledActiveId : internalActiveId;
  const setActiveId = useCallback(
    (id: string) => {
      if (isControlled) onActiveChange?.(id);
      else setInternalActiveId(id);
    },
    [isControlled, onActiveChange],
  );
  const orderRef = useRef(0);

  const register = useCallback((meta: DialogFormSectionMeta) => {
    setSections((prev) => {
      const existing = prev.find((s) => s.id === meta.id);
      if (
        existing &&
        existing.id === meta.id &&
        existing.group === meta.group &&
        existing.order === meta.order &&
        existing.hasError === meta.hasError &&
        existing.summary === meta.summary &&
        existing.label === meta.label &&
        existing.locked?.reason === meta.locked?.reason
      ) {
        return prev;
      }
      const next = prev.filter((s) => s.id !== meta.id);
      next.push(meta);
      return next;
    });
    return () => {
      setSections((prev) => prev.filter((s) => s.id !== meta.id));
    };
  }, []);

  const ordered = useMemo(() => {
    const groups = groupOrder ?? [...new Set(sections.map((s) => s.group))];
    return [...sections].sort((a, b) => {
      const ga = groups.indexOf(a.group);
      const gb = groups.indexOf(b.group);
      if (ga !== gb) return ga - gb;
      return a.order - b.order;
    });
  }, [sections, groupOrder]);

  useEffect(() => {
    // Default active = first non-locked section, once sections are known.
    if (activeId && ordered.some((s) => s.id === activeId && !s.locked)) return;
    const firstSelectable = ordered.find((s) => !s.locked);
    if (firstSelectable) setActiveId(firstSelectable.id);
  }, [ordered, activeId, setActiveId]);

  const setActive = useCallback(
    (id: string) => {
      const target = sections.find((s) => s.id === id);
      if (!target || target.locked) return;
      setActiveId(id);
    },
    [sections, setActiveId],
  );

  const contextValue = useMemo<DialogFormRailContextValue>(
    () => ({
      activeId,
      setActive,
      selectSection: setActive,
      register,
      sections: ordered,
    }),
    [activeId, setActive, register, ordered],
  );

  const grouped = useMemo(() => {
    const groups = groupOrder ?? [...new Set(ordered.map((s) => s.group))];
    return groups
      .map((group) => ({
        group,
        items: ordered.filter((s) => s.group === group),
      }))
      .filter((g) => g.items.length > 0);
  }, [ordered, groupOrder]);

  const nextOrder = useCallback(() => {
    orderRef.current += 1;
    return orderRef.current;
  }, []);

  return (
    <DialogFormRailContext.Provider value={contextValue}>
      <RailOrderContext.Provider value={nextOrder}>
        <div className={cn('flex min-h-0 flex-auto gap-6', className)}>
          <nav
            role="tablist"
            aria-orientation="vertical"
            className="w-56 shrink-0 space-y-4 overflow-y-auto border-r pr-4"
          >
            {grouped.map(({ group, items }) => (
              <div key={group} className="space-y-1">
                <p className="px-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {groupLabels?.[group] ?? group}
                </p>
                {items.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="tab"
                    aria-selected={activeId === s.id}
                    aria-disabled={s.locked ? true : undefined}
                    disabled={Boolean(s.locked)}
                    onClick={() => setActive(s.id)}
                    className={cn(
                      'flex w-full flex-col rounded-md px-2 py-1.5 text-left',
                      activeId === s.id && 'bg-muted',
                      s.locked && 'opacity-60',
                      !s.locked && 'hover:bg-muted/60',
                    )}
                  >
                    <span className="flex items-center gap-1 text-sm font-medium">
                      {s.label}
                      {s.locked ? (
                        <PiLock className="size-3.5" aria-hidden />
                      ) : null}
                      {s.hasError ? (
                        <PiWarningCircle
                          className="size-3.5 text-destructive"
                          aria-hidden
                        />
                      ) : null}
                    </span>
                    {(s.locked?.reason ?? s.summary) ? (
                      <span className="text-xs text-muted-foreground">
                        {s.locked ? s.locked.reason : s.summary}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            ))}
          </nav>
          {/* The pane is taken out of flow (absolute) so it never dictates the
              row height — only the nav (and the dialog's min-h floor) do. This
              keeps the height stable across section switches; a taller pane
              scrolls internally instead of growing the dialog. Wrapped in a
              plain div because ScrollArea forces its own `position: relative`. */}
          <div role="tabpanel" className="relative min-h-0 flex-auto">
            <div className="absolute inset-0">
              <ScrollArea className="h-full pr-4">
                <div className="px-px pt-px pb-1">{children}</div>
              </ScrollArea>
            </div>
          </div>
        </div>
      </RailOrderContext.Provider>
    </DialogFormRailContext.Provider>
  );
};

// Registration-order counter so sections without an explicit `order` keep DOM order.
const RailOrderContext = React.createContext<() => number>(() => 0);

interface DialogFormSectionProps {
  id: string;
  label: React.ReactNode;
  group: string;
  order?: number;
  summary?: React.ReactNode;
  locked?: { reason: React.ReactNode };
  hasError?: boolean;
  children?: React.ReactNode;
}

const DialogFormSection = ({
  id,
  label,
  group,
  order,
  summary,
  locked,
  hasError,
  children,
}: DialogFormSectionProps) => {
  const rail = React.useContext(DialogFormRailContext);
  const nextOrder = React.useContext(RailOrderContext);
  const assignedOrderRef = useRef<number | null>(null);
  if (!rail) {
    throw new Error(
      'DialogForm.Section must be used within a <DialogForm.Rail>.',
    );
  }
  if (assignedOrderRef.current === null) {
    assignedOrderRef.current = order ?? nextOrder();
  }
  const { register, activeId } = rail;
  const lockedReason = locked?.reason;

  useEffect(() => {
    return register({
      id,
      label,
      group,
      order: assignedOrderRef.current as number,
      summary,
      locked: lockedReason !== undefined ? { reason: lockedReason } : undefined,
      hasError,
    });
    // Depend on lockedReason (string), reconstructing the locked object inside
    // the effect, so an inline locked={{reason}} prop's changing identity does
    // not retrigger registration (which would infinite-loop).
  }, [register, id, label, group, summary, lockedReason, hasError]);

  if (activeId !== id) return null;
  return <>{children}</>;
};

interface DialogFormStillNeededProps {
  phrase: string | null;
  prefix?: string;
  className?: string;
}

const DialogFormStillNeeded = ({
  phrase,
  prefix = 'Still needed:',
  className,
}: DialogFormStillNeededProps) => {
  if (!phrase) return null;
  return (
    <p
      className={cn(
        'mr-auto self-center text-sm text-muted-foreground',
        className,
      )}
    >
      {prefix} {phrase}
    </p>
  );
};

export const DialogForm = Object.assign(DialogFormRoot, {
  Header: DialogFormHeader,
  Title: DialogFormTitle,
  Description: DialogFormDescription,
  Body: DialogFormBody,
  Footer: DialogFormFooter,
  Cancel: DialogFormCancel,
  Submit: DialogFormSubmit,
  Rail: DialogFormRail,
  Section: DialogFormSection,
  StillNeeded: DialogFormStillNeeded,
});
